import { useState, useEffect } from "react";

const API_URL =
  "https://api.cricapi.com/v1/currentMatches?apikey=bd5bb499-e994-4cb7-9c6f-fe27a78d24fe";

const theme = {
  bg: "#0d0d0f",
  bgCard: "#16181c",
  bgHover: "#1e2026",
  border: "rgba(255,255,255,0.08)",
  borderHov: "rgba(255,255,255,0.18)",
  textPri: "#f0f0f2",
  textSec: "#8a8d96",
  textHint: "#55575e",
  danger: "#f87171",
};

function isIPL(match) {
  const name = (match.name || "").toLowerCase();
  const series = (match.series_id || "").toLowerCase();
  return (
    name.includes("ipl") ||
    name.includes("indian premier league") ||
    series.includes("ipl")
  );
}

function getStatus(match) {
  if (!match.matchStarted) return "upcoming";
  if (match.matchStarted && !match.matchEnded) return "live";
  return "done";
}

function Badge({ status }) {
  const styles = {
    live: {
      background: "rgba(248,113,113,0.15)",
      color: "#fca5a5",
      border: "0.5px solid rgba(248,113,113,0.35)",
    },
    done: {
      background: "rgba(52,211,153,0.12)",
      color: "#6ee7b7",
      border: "0.5px solid rgba(52,211,153,0.3)",
    },
    upcoming: {
      background: "rgba(96,165,250,0.12)",
      color: "#93c5fd",
      border: "0.5px solid rgba(96,165,250,0.3)",
    },
  };
  const labels = { live: "● Live", done: "Done", upcoming: "Upcoming" };
  return (
    <span
      style={{
        ...styles[status],
        fontSize: 10,
        fontWeight: 500,
        padding: "2px 8px",
        borderRadius: 999,
        whiteSpace: "nowrap",
        flexShrink: 0,
        letterSpacing: "0.02em",
      }}
    >
      {labels[status]}
    </span>
  );
}

function TeamRow({ team, scores }) {
  const sc = scores?.find(
    (s) =>
      s.inning
        .toLowerCase()
        .includes((team.name || "").toLowerCase().split(" ")[0]) ||
      s.inning.toLowerCase().includes((team.shortname || "").toLowerCase())
  );

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <img
          src={team.img || ""}
          alt={team.shortname}
          onError={(e) => (e.target.style.opacity = "0.15")}
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            objectFit: "contain",
            border: `0.5px solid ${theme.border}`,
            background: "#1e2026",
          }}
        />
        <span style={{ fontSize: 13, fontWeight: 500, color: theme.textPri }}>
          {team.shortname || team.name}
        </span>
      </div>
      <div>
        {sc ? (
          <>
            <span
              style={{ fontSize: 16, fontWeight: 500, color: theme.textPri }}
            >
              {sc.r}/{sc.w}
            </span>
            <span style={{ fontSize: 11, color: theme.textSec, marginLeft: 3 }}>
              ({sc.o})
            </span>
          </>
        ) : (
          <span style={{ fontSize: 11, color: theme.textHint }}>—</span>
        )}
      </div>
    </div>
  );
}

function MatchCard({ match }) {
  const [hovered, setHovered] = useState(false);
  const status = getStatus(match);
  const teams = match.teamInfo || [];
  const scores = match.score || [];

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? theme.bgHover : theme.bgCard,
        border: `0.5px solid ${hovered ? theme.borderHov : theme.border}`,
        borderRadius: 12,
        padding: "1rem 1.25rem",
        transition: "background 0.15s, border-color 0.15s",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontSize: 12,
            color: theme.textSec,
            flex: 1,
            lineHeight: 1.4,
          }}
        >
          {match.name || ""}
        </span>
        <Badge status={status} />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 11,
          color: theme.textHint,
          marginBottom: 8,
        }}
      >
        <span>{match.venue || ""}</span>
        <span>{match.date || ""}</span>
      </div>

      <div
        style={{ height: "0.5px", background: theme.border, margin: "8px 0" }}
      />

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {teams.length > 0 ? (
          teams.map((team, i) => (
            <TeamRow key={i} team={team} scores={scores} />
          ))
        ) : (
          <span style={{ fontSize: 12, color: theme.textHint }}>
            No team info
          </span>
        )}
      </div>

      <div
        style={{ height: "0.5px", background: theme.border, margin: "8px 0" }}
      />

      <div style={{ fontSize: 12, color: theme.textSec }}>
        {match.status || ""}
      </div>
    </div>
  );
}

function StatPill({ color, label, count }) {
  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}
    >
      <div
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: color,
          flexShrink: 0,
        }}
      />
      <span style={{ color: theme.textSec }}>{label}</span>
      &nbsp;
      <span style={{ fontWeight: 500, color: theme.textPri }}>{count}</span>
    </div>
  );
}

export default function IPLCricketLive() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState("");

  useEffect(() => {
    fetch(API_URL)
      .then((r) => r.json())
      .then((res) => {
        const all = res.data || [];
        setMatches(all.filter(isIPL));
        const now = new Date();
        setLastUpdated(
          `Updated ${now.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}`
        );
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const live = matches.filter((m) => getStatus(m) === "live").length;
  const upcoming = matches.filter((m) => getStatus(m) === "upcoming").length;
  const done = matches.filter((m) => getStatus(m) === "done").length;

  return (
    <div
      style={{
        fontFamily: "system-ui, sans-serif",
        background: theme.bg,
        minHeight: "100vh",
        padding: "0 1.25rem 2rem",
        color: theme.textPri,
      }}
    >
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 12,
          padding: "1.25rem 0 0.75rem",
          borderBottom: `0.5px solid ${theme.border}`,
        }}
      >
        <h1
          style={{
            fontSize: 22,
            fontWeight: 500,
            margin: 0,
            color: theme.textPri,
          }}
        >
          IPL 2025
        </h1>
        <span style={{ fontSize: 12, color: theme.textHint }}>
          {lastUpdated}
        </span>
      </div>

      {/* Stats row */}
      {!loading && !error && (
        <div
          style={{
            display: "flex",
            gap: 16,
            padding: "10px 0",
            flexWrap: "wrap",
            borderBottom: `0.5px solid ${theme.border}`,
          }}
        >
          <StatPill color="#f87171" label="Live" count={live} />
          <StatPill color="#60a5fa" label="Upcoming" count={upcoming} />
          <StatPill color="#34d399" label="Completed" count={done} />
          <StatPill
            color={theme.textHint}
            label="Total"
            count={matches.length}
          />
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div
          style={{
            padding: "3rem 0",
            textAlign: "center",
            color: theme.textSec,
            fontSize: 14,
          }}
        >
          <div
            style={{
              width: 22,
              height: 22,
              border: `1.5px solid ${theme.border}`,
              borderTopColor: theme.textSec,
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
              margin: "0 auto 12px",
            }}
          />
          Fetching IPL scores...
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ color: theme.danger, fontSize: 13, padding: "1rem 0" }}>
          Failed to load scores: {error}
        </div>
      )}

      {/* Empty */}
      {!loading && !error && matches.length === 0 && (
        <div
          style={{
            color: theme.textSec,
            fontSize: 13,
            padding: "2rem 0",
            textAlign: "center",
          }}
        >
          No IPL matches found right now
        </div>
      )}

      {/* Match grid */}
      {!loading && !error && matches.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 10,
            paddingTop: 12,
          }}
        >
          {matches.map((match) => (
            <MatchCard key={match.id} match={match} />
          ))}
        </div>
      )}
    </div>
  );
}
